import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { capitalCase } from 'change-case';
import dayjs from 'dayjs';
import evaluateAbvLabel from 'util/evaluateAbvLabel';
import { resolveLabelBarcode } from 'util/label-barcode-resolve';
import {
  renderCODE128,
  renderEAN13,
  renderKegRightEan13,
  renderPalletPrimaryBarcode,
} from 'util/labelPreviewBarcode';
import {
  CERTIFIED_FRESH_LOGO_URL,
  GP_LOGO_URL,
  HUAXI_REGISTRATION_NUMBER,
  LABEL_PREVIEW_SIZES_MM,
  LABEL_UNIT_DETAILS,
} from 'config/labelUnitDetails';
import './LabelPreview.less';

const PIXELS_PER_MM = 3.779528;

function mmToPx(mm) {
  return mm * PIXELS_PER_MM;
}

function formatPreviewDate(date) {
  if (!date) return '—';
  const parsed = dayjs(date);
  return parsed.isValid() ? parsed.format('DD/MM/YYYY') : '—';
}

function useBarcodeRenderer(renderFn, value) {
  const svgRef = useRef(null);
  const [valid, setValid] = useState(false);

  useEffect(() => {
    const svg = svgRef.current;
    if (!svg) return undefined;
    while (svg.firstChild) {
      svg.removeChild(svg.firstChild);
    }
    if (!value) {
      setValid(false);
      return undefined;
    }
    setValid(renderFn(svg, value));
    return undefined;
  }, [renderFn, value]);

  return { svgRef, valid };
}

function BarcodeSvg({ value, renderFn, className }) {
  const { svgRef, valid } = useBarcodeRenderer(renderFn, value);

  if (!value) {
    return (
      <div className="label-preview__barcode-placeholder">
        No barcode
      </div>
    );
  }

  return (
    <svg
      ref={svgRef}
      className={className}
      role="img"
      aria-label={valid ? `Barcode ${value}` : 'Invalid barcode'}
    />
  );
}

function ScaledLabelFrame({ widthMm, heightMm, children }) {
  const hostRef = useRef(null);
  const [scale, setScale] = useState(1);

  const widthPx = mmToPx(widthMm);
  const heightPx = mmToPx(heightMm);

  useEffect(() => {
    const host = hostRef.current;
    if (!host) return undefined;

    const updateScale = () => {
      const availableWidth = host.clientWidth;
      const availableHeight = host.clientHeight;
      if (!availableWidth || !availableHeight) return;
      setScale(Math.min(
        availableWidth / widthPx,
        availableHeight / heightPx,
      ));
    };

    updateScale();
    const observer = new ResizeObserver(updateScale);
    observer.observe(host);
    return () => observer.disconnect();
  }, [widthPx, heightPx]);

  return (
    <div ref={hostRef} className="label-preview__scale-host">
      <div
        className="label-preview__label-frame"
        style={{
          width: widthPx,
          height: heightPx,
          transform: `scale(${scale})`,
        }}
      >
        {children}
      </div>
    </div>
  );
}

ScaledLabelFrame.propTypes = {
  widthMm: PropTypes.number.isRequired,
  heightMm: PropTypes.number.isRequired,
  children: PropTypes.node.isRequired,
};

function SlabCasePreview({
  huaxi,
  name,
  oneLiner,
  tapBadgeUrl,
  certifiedFresh,
  baseUnitQuantity,
  baseUnitLabel,
  abvLabel,
  weight,
  dateTypeLabel,
  formattedDate,
  gyle,
  barcodeNumber,
}) {
  if (huaxi) {
    return (
      <div className="label-preview__slab-case label-preview__slab-case--huaxi">
        <div className="label-preview__slab-case-body">
          <div className="label-preview__badge-row">
            {tapBadgeUrl ? (
              <img height={50} src={tapBadgeUrl} alt="" />
            ) : (
              <div className="label-preview__badge-placeholder" />
            )}
          </div>
          <h1 className="label-preview__title">{name}</h1>
          <h2 className="label-preview__subtitle">{oneLiner || '\u00A0'}</h2>
          <div className="label-preview__unit-line">
            {baseUnitQuantity}
            {' '}
            x
            {' '}
            {baseUnitLabel}
            {abvLabel ? ` @ ${abvLabel} ABV` : ''}
          </div>
          <div>{`Gross weight: ${weight ?? '—'}kg`}</div>
          <div>
            Packing Date:
            {' '}
            {formattedDate}
          </div>
          <div>Shelf Life: 24个月</div>
          <div className="label-preview__huaxi-reg">
            生产企业在华注册编号：
            {HUAXI_REGISTRATION_NUMBER}
          </div>
          <div className="label-preview__gyle-line">
            Gyle: #
            {gyle || '—'}
          </div>
        </div>
        <div className="label-preview__barcode-row">
          <BarcodeSvg value={barcodeNumber} renderFn={renderEAN13} />
        </div>
      </div>
    );
  }

  return (
    <div className="label-preview__slab-case">
      <div className="label-preview__slab-case-body">
        <div className="label-preview__badge-row">
          {tapBadgeUrl ? (
            <img height={52} src={tapBadgeUrl} alt="" />
          ) : (
            <div className="label-preview__badge-placeholder" />
          )}
          {certifiedFresh && (
            <img height={52} src={CERTIFIED_FRESH_LOGO_URL} alt="" />
          )}
        </div>
        <h1 className="label-preview__title label-preview__title--standard">{name}</h1>
        <h2 className="label-preview__subtitle label-preview__subtitle--standard">{oneLiner || '\u00A0'}</h2>
        <div className="label-preview__unit-line label-preview__unit-line--standard">
          {baseUnitQuantity}
          {' '}
          x
          {' '}
          {baseUnitLabel}
          {abvLabel ? ` @ ${abvLabel} ABV` : ''}
        </div>
        <div>{`Gross weight: ${weight ?? '—'}kg`}</div>
        <div>
          {dateTypeLabel}
          :
          {' '}
          {formattedDate}
        </div>
        <div>
          Gyle: #
          {gyle || '—'}
        </div>
      </div>
      <div className="label-preview__barcode-row">
        <BarcodeSvg value={barcodeNumber} renderFn={renderEAN13} />
      </div>
    </div>
  );
}

SlabCasePreview.propTypes = {
  huaxi: PropTypes.bool,
  name: PropTypes.string.isRequired,
  oneLiner: PropTypes.string,
  tapBadgeUrl: PropTypes.string,
  certifiedFresh: PropTypes.bool,
  baseUnitQuantity: PropTypes.number,
  baseUnitLabel: PropTypes.string,
  abvLabel: PropTypes.string,
  weight: PropTypes.number,
  dateTypeLabel: PropTypes.string,
  formattedDate: PropTypes.string.isRequired,
  gyle: PropTypes.string,
  barcodeNumber: PropTypes.string,
};

SlabCasePreview.defaultProps = {
  huaxi: false,
  oneLiner: '',
  tapBadgeUrl: undefined,
  certifiedFresh: false,
  baseUnitQuantity: undefined,
  baseUnitLabel: undefined,
  abvLabel: undefined,
  weight: undefined,
  dateTypeLabel: 'Date',
  gyle: undefined,
  barcodeNumber: undefined,
};

function KegBadge({ tapBadgeUrl, size }) {
  if (tapBadgeUrl) {
    return (
      <img
        src={tapBadgeUrl}
        height={size}
        alt=""
        className="label-preview__keg-badge"
      />
    );
  }
  return <div className="label-preview__keg-badge label-preview__badge-placeholder" />;
}

KegBadge.propTypes = {
  tapBadgeUrl: PropTypes.string,
  size: PropTypes.number.isRequired,
};

KegBadge.defaultProps = {
  tapBadgeUrl: undefined,
};

export function KegPreview({
  name,
  oneLiner,
  tapBadgeUrl,
  unitSize,
  abvLabel,
  gyle,
  dateTypeLabel,
  formattedDate,
  showUnitBarcode,
  barcodeNumber,
}) {
  const badgeSize = showUnitBarcode ? 58 : 90;
  const badge = <KegBadge tapBadgeUrl={tapBadgeUrl} size={badgeSize} />;

  return (
    <div className={`label-preview__keg${showUnitBarcode ? ' label-preview__keg--right' : ''}`}>
      {badge}
      <div className="label-preview__keg-body">
        <h1 className="label-preview__keg-title">{name}</h1>
        <h2 className="label-preview__keg-subtitle">{oneLiner || '\u00A0'}</h2>
        <div className="label-preview__keg-line">
          {unitSize}
          L
          {abvLabel ? ` @ ${abvLabel} ABV` : ''}
        </div>
        <div className="label-preview__keg-line">
          Gyle: #
          {gyle || '—'}
        </div>
        <div className="label-preview__keg-line">
          {dateTypeLabel}
          :
          {' '}
          {formattedDate}
        </div>
        <div className="label-preview__keg-line">
          Keg: #1
        </div>
      </div>
      {showUnitBarcode ? (
        <div className="label-preview__keg-barcode">
          <BarcodeSvg value={barcodeNumber} renderFn={renderKegRightEan13} />
        </div>
      ) : null}
    </div>
  );
}

KegPreview.propTypes = {
  name: PropTypes.string.isRequired,
  oneLiner: PropTypes.string,
  tapBadgeUrl: PropTypes.string,
  unitSize: PropTypes.string,
  abvLabel: PropTypes.string,
  gyle: PropTypes.string,
  dateTypeLabel: PropTypes.string,
  formattedDate: PropTypes.string.isRequired,
  showUnitBarcode: PropTypes.bool,
  barcodeNumber: PropTypes.string,
};

KegPreview.defaultProps = {
  oneLiner: '',
  tapBadgeUrl: undefined,
  unitSize: undefined,
  abvLabel: undefined,
  gyle: undefined,
  dateTypeLabel: 'Date',
  showUnitBarcode: false,
  barcodeNumber: undefined,
};

function PalletPreview({
  name,
  oneLiner,
  tapBadgeUrl,
  abvLabel,
  palletQuantity,
  palletType,
  palletContentsLabel,
  dearSku,
  dateTypeLabel,
  formattedDate,
  gyle,
  primaryBarcodeNumber,
}) {
  const requiresBarcode = palletType !== 'keg';

  return (
    <div className="label-preview__pallet">
      <div className="label-preview__pallet-top">
        <div className="label-preview__pallet-logos">
          <img height={100} src={GP_LOGO_URL} alt="" />
          {tapBadgeUrl ? (
            <img src={tapBadgeUrl} height={120} alt="" />
          ) : (
            <div className="label-preview__pallet-badge-placeholder" />
          )}
        </div>
        <div className="label-preview__pallet-text">
          <h1 className="label-preview__pallet-title">{name}</h1>
          <h2 className="label-preview__pallet-subtitle">{oneLiner || '\u00A0'}</h2>
          <div className="label-preview__pallet-details">
            {abvLabel && (
              <div>
                {abvLabel}
                {' '}
                ABV
              </div>
            )}
            <div>
              Quantity:
              {' '}
              {palletQuantity == null || palletQuantity === '' ? '___' : palletQuantity}
              {' '}
              {palletType}
              s
            </div>
            <div>
              (
              {palletContentsLabel}
              )
            </div>
            {dearSku && (
              <div className="label-preview__pallet-sku">
                SKU:
                {' '}
                {dearSku}
              </div>
            )}
          </div>
        </div>
      </div>
      <div className="label-preview__pallet-bottom">
        <div className="label-preview__pallet-codes">
          <div className="label-preview__pallet-code-block">
            <div>
              <span className="label-preview__pallet-code-label">
                {dateTypeLabel}
                :
                {' '}
              </span>
              {formattedDate}
            </div>
            <BarcodeSvg value={formattedDate !== '—' ? formattedDate : null} renderFn={renderCODE128} />
          </div>
          <div className="label-preview__pallet-code-block">
            <div>
              <span className="label-preview__pallet-code-label">
                Gyle:
                {' '}
              </span>
              #
              {gyle || '—'}
            </div>
            <BarcodeSvg value={gyle || null} renderFn={renderCODE128} />
          </div>
        </div>
        {requiresBarcode && (
          <div className="label-preview__pallet-primary-barcode">
            <BarcodeSvg
              value={primaryBarcodeNumber}
              renderFn={renderPalletPrimaryBarcode}
            />
          </div>
        )}
      </div>
    </div>
  );
}

PalletPreview.propTypes = {
  name: PropTypes.string.isRequired,
  oneLiner: PropTypes.string,
  tapBadgeUrl: PropTypes.string,
  abvLabel: PropTypes.string,
  palletQuantity: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  palletType: PropTypes.string,
  palletContentsLabel: PropTypes.string,
  dearSku: PropTypes.string,
  dateTypeLabel: PropTypes.string,
  formattedDate: PropTypes.string.isRequired,
  gyle: PropTypes.string,
  primaryBarcodeNumber: PropTypes.string,
};

PalletPreview.defaultProps = {
  oneLiner: '',
  tapBadgeUrl: undefined,
  abvLabel: undefined,
  palletQuantity: undefined,
  palletType: undefined,
  palletContentsLabel: '',
  dearSku: undefined,
  dateTypeLabel: 'Date',
  gyle: undefined,
  primaryBarcodeNumber: undefined,
};

const LabelPreview = ({
  labelType,
  product,
  formValues,
  huaxiMode,
  resolvedOption,
}) => {
  if (!product || !labelType) {
    return null;
  }

  const {
    customProductName,
    gyle,
    date,
    dateType,
    unitSize,
    certifiedFresh,
    quantity,
    palletType: formPalletType,
    showUnitBarcode,
  } = formValues || {};

  const displayName = customProductName || product.name || 'Product name';
  const formattedDate = formatPreviewDate(date);
  const dateTypeLabel = dateType ? capitalCase(dateType) : 'Date';
  const abvLabel = evaluateAbvLabel({
    abv: product.abv,
    abvMin: product.abvMin,
    abvMax: product.abvMax,
  });

  const effectiveUnitSize = unitSize || resolvedOption?.unitSize;
  const palletType = labelType === 'pallet'
    ? (formPalletType || resolvedOption?.packagingType)
    : resolvedOption?.packagingType;

  const barcodeNumber = effectiveUnitSize
    ? resolveLabelBarcode(product, {
      labelType,
      unitSize: effectiveUnitSize,
      palletType,
    })
    : null;

  const sizeMm = LABEL_PREVIEW_SIZES_MM[labelType] || LABEL_PREVIEW_SIZES_MM.slab;

  let labelContent = null;

  if (labelType === 'keg') {
    labelContent = (
      <KegPreview
        name={displayName}
        oneLiner={product.oneLiner}
        tapBadgeUrl={product.tapBadgeUrl}
        unitSize={effectiveUnitSize}
        abvLabel={abvLabel}
        gyle={gyle}
        dateTypeLabel={dateTypeLabel}
        formattedDate={formattedDate}
        showUnitBarcode={!!showUnitBarcode}
        barcodeNumber={barcodeNumber}
      />
    );
  } else if (labelType === 'pallet') {
    const unitDetails = palletType && effectiveUnitSize
      ? LABEL_UNIT_DETAILS[palletType]?.[effectiveUnitSize]
      : null;
    const baseUnitQuantity = unitDetails?.quantity;
    const baseUnitLabel = unitDetails?.label;
    const palletContentsLabel = baseUnitLabel
      ? `${baseUnitQuantity > 1 ? `${baseUnitQuantity} x ` : ''}${baseUnitLabel}`
      : '—';

    labelContent = (
      <PalletPreview
        name={displayName.toUpperCase()}
        oneLiner={product.oneLiner}
        tapBadgeUrl={product.tapBadgeUrl}
        abvLabel={abvLabel}
        palletQuantity={quantity}
        palletType={palletType}
        palletContentsLabel={palletContentsLabel}
        dearSku={product.dearSku}
        dateTypeLabel={dateTypeLabel}
        formattedDate={formattedDate}
        gyle={gyle}
        primaryBarcodeNumber={barcodeNumber}
      />
    );
  } else {
    const unitDetails = LABEL_UNIT_DETAILS[labelType]?.[effectiveUnitSize];
    labelContent = (
      <SlabCasePreview
        huaxi={labelType === 'slab' && huaxiMode}
        name={displayName}
        oneLiner={product.oneLiner}
        tapBadgeUrl={product.tapBadgeUrl}
        certifiedFresh={certifiedFresh}
        baseUnitQuantity={unitDetails?.quantity}
        baseUnitLabel={unitDetails?.label}
        abvLabel={abvLabel}
        weight={unitDetails?.weight}
        dateTypeLabel={dateTypeLabel}
        formattedDate={formattedDate}
        gyle={gyle}
        barcodeNumber={barcodeNumber}
      />
    );
  }

  return (
    <div className="label-preview">
      <ScaledLabelFrame widthMm={sizeMm.width} heightMm={sizeMm.height}>
        <div className="label-preview__label-surface">
          {labelContent}
        </div>
      </ScaledLabelFrame>
    </div>
  );
};

LabelPreview.propTypes = {
  labelType: PropTypes.oneOf(['keg', 'case', 'slab', 'pallet']),
  product: PropTypes.shape({
    name: PropTypes.string,
    oneLiner: PropTypes.string,
    tapBadgeUrl: PropTypes.string,
    abv: PropTypes.number,
    abvMin: PropTypes.number,
    abvMax: PropTypes.number,
    dearSku: PropTypes.string,
    skus: PropTypes.arrayOf(PropTypes.object),
  }),
  formValues: PropTypes.shape({
    customProductName: PropTypes.string,
    gyle: PropTypes.string,
    date: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
    dateType: PropTypes.string,
    unitSize: PropTypes.string,
    certifiedFresh: PropTypes.bool,
    quantity: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    palletType: PropTypes.string,
    showUnitBarcode: PropTypes.bool,
  }),
  huaxiMode: PropTypes.bool,
  resolvedOption: PropTypes.shape({
    unitSize: PropTypes.string,
    packagingType: PropTypes.string,
  }),
};

LabelPreview.defaultProps = {
  labelType: undefined,
  product: null,
  formValues: {},
  huaxiMode: false,
  resolvedOption: null,
};

export default LabelPreview;
